import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, roleHome } from "../context/AuthContext";
import { formatError } from "../lib/api";
import { Brand } from "../components/Brand";
import { toast } from "sonner";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = mode === "login"
        ? await login(email, password)
        : await register({ name, email, password });
      toast.success(`Welcome${user.name ? ", " + user.name.split(" ")[0] : ""}!`);
      navigate(roleHome(user.role));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <div className="relative"><Brand light /></div>
        <div className="relative max-w-md">
          <h1 className="font-heading text-4xl font-extrabold tracking-tight leading-tight">Crack your next interview with confidence.</h1>
          <p className="mt-4 text-slate-300 leading-relaxed">AI & expert mock interviews, embedded coding assessments and a measurable Einstein Score.</p>
          <div className="mt-8 flex items-center gap-2 text-sm text-blue-200"><ShieldCheck className="h-4 w-4" /> Secure, role-based access</div>
        </div>
        <div className="relative text-xs text-slate-400">© 2026 Mock2Crack</div>
      </div>

      {/* Right form */}
      <div className="flex flex-col items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden mb-8"><Brand /></div>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-ink">{mode === "login" ? "Welcome back" : "Create your account"}</h2>
          <p className="text-ink-soft mt-1 text-sm">{mode === "login" ? "Log in to continue your interview prep." : "Start your journey to a higher Einstein Score."}</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-xs font-semibold text-ink-soft">Full name</label>
                <input data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} required
                  className="mt-1.5 w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand" placeholder="Jane Doe" />
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-ink-soft">Email</label>
              <input data-testid="email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="mt-1.5 w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand" placeholder="you@example.com" />
            </div>
            <div>
              <label className="text-xs font-semibold text-ink-soft">Password</label>
              <input data-testid="password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="mt-1.5 w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand" placeholder="••••••••" />
            </div>
            <button data-testid="submit-auth" disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-3.5 rounded-full shadow-ambient disabled:opacity-60 transition-colors">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{mode === "login" ? "Log in" : "Create account"} <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>

          <div className="mt-5 text-sm text-ink-soft text-center">
            {mode === "login" ? (
              <>New candidate? <button onClick={() => setMode("register")} className="font-bold text-brand" data-testid="switch-register">Create an account</button></>
            ) : (
              <>Already have an account? <button onClick={() => setMode("login")} className="font-bold text-brand" data-testid="switch-login">Log in</button></>
            )}
          </div>
          <div className="mt-6 text-center"><Link to="/" className="text-xs text-ink-faint hover:text-ink">← Back to home</Link></div>
        </div>
      </div>
    </div>
  );
}
